import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { COLOR_MAP } from "../../constants/ColorNames";
import FounditIcon from "../../constants/icons/FounditIcon";
import GoogleMailIcon from "../../constants/icons/GoogleMailIcon";
import OutlookMailIcon from "../../constants/icons/OutlookMailIcon";
import { useGetAuthenticateEmailMutation } from "../../redux/services/folderName";
import { getErrorMsg } from "../../utils/whatsAppUtils";

export type TProvider = "ews" | "Gmail";

const providers: {
  label: string;
  id: string;
  Icon: any;
  provider: TProvider;
}[] = [
  {
    label: "Gmail",
    id: "gmail",
    provider: "Gmail",
    Icon: GoogleMailIcon,
  },
  {
    label: "Outlook",
    id: "outlook",
    provider: "ews",
    Icon: OutlookMailIcon,
  },
];

const ConfigureMail = () => {
  const [providerVal, setProviderVal] = useState<TProvider | null>(null);
  const urlParams = new URLSearchParams(window.location.search);
  const page = urlParams.get("page");

  const [
    getAuthenticateEmailData,
    { data, isLoading, isError, error, isSuccess },
  ] = useGetAuthenticateEmailMutation();

  const handleClickProvider = (provider: TProvider) => {
    setProviderVal(provider);
    getAuthenticateEmailData({
      page: page || "",
      provider,
    });
  };

  useEffect(() => {
    if (isSuccess && data?.redirectURL) {
      window.location.href = data.redirectURL;
    }
  }, [data, isSuccess]);

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: COLOR_MAP.GREY,
        height: "100vh",
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
      }}
    >
      <Box sx={{ width: "100%", px: 1 }}>
        <Box sx={{ width: "110px", height: "30px" }}>
          <FounditIcon />
        </Box>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            boxShadow: `rgba(0, 0, 0, 0.2) 0px 2px 6px 0px`,
            width: { xs: "100%", sm: "360px" },
            height: "300px",
            backgroundColor: "white",
            p: 3,
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              my: 2,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: "20px" }}>
              Select your email provider
            </Typography>
          </Box>

          <Box sx={{ py: 2 }}>
            {providers?.map(({ id, label, Icon, provider }, index) => (
              <>
                <Box
                  sx={{
                    display: "flex",
                    py: 1,
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    "&: hover": {
                      backgroundColor: COLOR_MAP.GREY_8,
                    },
                    borderRadius: "8px",
                  }}
                  key={id}
                  onClick={() => handleClickProvider(provider)}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{ mr: 1 }}>
                      <Icon height="38" width="38" />
                    </Box>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                      {label}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {providerVal === provider && isLoading ? (
                      <CircularProgress
                        size={18}
                        sx={{ color: COLOR_MAP.GREY }}
                      />
                    ) : (
                      <ChevronRightIcon sx={{ fontSize: 18 }} />
                    )}
                  </Box>
                </Box>
                {index !== providers?.length - 1 && (
                  <Box sx={{ width: "100%" }}>
                    <Divider />
                  </Box>
                )}
              </>
            ))}
          </Box>
          {isError && (
            <Typography
              variant="h6"
              sx={{
                color: COLOR_MAP.ERROR,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {getErrorMsg({ error }) ||
                "Something went wrong. Please try again."}
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ConfigureMail;
